import { type FC } from 'react';
import classnames from 'classnames';
import { useIntl } from 'dibs-react-intl';

import dibsCss from 'dibs-css';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { CM, IN, MEASUREMENT_UNITS, type MeasurementUnit } from 'dibs-intl/exports/units';

import { dimensionUnitAriaLabel } from '../pdpMessages.tsx';
import { trackPdpDimensionToggle } from '../tracking/pdpItemModuleTracking.ts';
import styles from './PdpDimensionUnitToggle.module.css';

type Props = {
    value: MeasurementUnit;
    onChange: (unit: MeasurementUnit) => void;
};

export const PdpDimensionUnitToggle: FC<Props> = ({ value, onChange }) => {
    const intl = useIntl();
    const activeIndex = MEASUREMENT_UNITS.indexOf(value);

    return (
        // Link with onClick renders an unstyled <button>. dibs-elements Button sets a fixed height,
        // padding, and colors with single-class rules, the same specificity as dibs-css utilities,
        // so overriding them to fit this pill would depend on stylesheet load order.
        <Link
            underline="none"
            buyerLinkType="standardLinkNoEffect"
            ariaLabel={intl.formatMessage(dimensionUnitAriaLabel, { measurementUnit: value })}
            dataTn="dimension-unit-toggle"
            className={classnames(dibsCss.flex, dibsCss.flexShrink0, dibsCss.roundedXxsmall)}
            onClick={() => {
                trackPdpDimensionToggle();
                onChange(value === IN ? CM : IN);
            }}
        >
            {/* Link's unstyled-button reset sets `background: none`, so the pill background lives on an inner element. */}
            <span
                className={classnames(
                    dibsCss.relative,
                    dibsCss.flex,
                    dibsCss.gap0,
                    dibsCss.itemsStart,
                    dibsCss.bgMoonstone,
                    dibsCss.roundedXxsmall
                )}
            >
                <span
                    aria-hidden="true"
                    className={classnames(
                        styles.indicator,
                        dibsCss.absolute,
                        dibsCss.top0,
                        dibsCss.left0,
                        dibsCss.hFull,
                        dibsCss.pointerEventsNone,
                        dibsCss.bgNoir,
                        dibsCss.roundedXxsmall
                    )}
                    style={{ transform: `translateX(${activeIndex * 100}%)` }}
                />
                {MEASUREMENT_UNITS.map(unit => (
                    <span
                        key={unit}
                        aria-hidden="true"
                        data-tn={`dimension-unit-${unit.toLowerCase()}`}
                        className={classnames(
                            styles.option,
                            dibsCss.relative,
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.itemsCenter,
                            dibsCss.justifyCenter,
                            dibsCss.ptXxxsmall,
                            dibsCss.pxXxsmall,
                            dibsCss.pb0,
                            dibsCss.roundedXxsmall,
                            dibsCss.sassyFontSubHeaderSmallHeavy,
                            dibsCss.textCenter,
                            dibsCss.whitespaceNowrap,
                            unit === value ? dibsCss.textWhite : dibsCss.textNoir
                        )}
                    >
                        {unit.toLowerCase()}
                    </span>
                ))}
            </span>
        </Link>
    );
};
